<template>
  <div
    :class="[
      'service-location',
      {
        'service-location--favorite': serviceID === favoriteStoreNumber && !selectedServiceNumber,
        'service-location--selected': serviceID === selectedServiceNumber,
        'service-location--checkout': !isPDP,
        'service-location--hide-border': hideBorder,
        'service-location--checkout-address-item': checkoutAddressItem,
      },
    ]"
    role="button"
    tabindex="0"
    @click="onServiceClick"
    @keydown.enter="onServiceKeydownEnter"
  >
    <div class="sc-address-wrapper">
      <div class="sc-address">
        <fn-headline
          type="h3"
          size="h5"
          same-size-mobile
          no-margin-bottom
        >
          {{ service.displayName || service.firstName }}
        </fn-headline>

        <fn-store-address
          v-if="Object.keys(service?.address || {}).length"
          :address="service.address"
        />
      </div>
      <span v-if="service.formattedDistance">{{ service.formattedDistance }}</span>
    </div>

    <fn-store-opening-time-new
      v-if="service?.openingHours && !isPudo && !checkoutAddressItem"
      :store="service"
    />

    <fn-week-working-time-list
      v-if="service?.weekWorkingTime && isPudo"
      :week-working-time="service.weekWorkingTime"
    />

    <p
      v-if="service?.stockLevelStatus === 'lowStock'"
      class="low-quantity-info"
      v-html="$t('product.availability.resultLowInfo')"
    />

    <fn-product-availability-store-check
      v-if="!isPudo && clickAndCollectEnabled"
      :is-click-and-collect-eligible="isClickAndCollectEligible"
    />

    <fn-product-shipping-duration
      v-if="isPDP"
      :store-availability="service.storeStock"
      :shipping-information="product.shippingInformation"
      :stock="service.storeStock"
      is-pdp
    />
  </div>
</template>

<script>
import { inStock } from '@/utils/stock';

export default defineComponent({
  props: {
    service: {
      type: Object,
      required: true,
    },
    detailsLinkVisible: {
      type: Boolean,
    },
    favoriteStoreNumber: {
      type: String,
      default: null,
    },
    selectedServiceNumber: {
      type: String,
      default: null,
    },
    isPDP: {
      type: Boolean,
    },
    isPudo: {
      type: Boolean,
    },
    hideBorder: {
      type: Boolean,
    },
    checkoutAddressItem: {
      type: Boolean,
    },
    product: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['serviceCardClick', 'plpServiceCardClick', 'focusConfirmButton'],
  computed: {
    ...mapState(useMainStore, {
      clickAndCollectEnabled: 'getClickAndCollectEnabled',
    }),

    serviceID() {
      return this.service?.storeNumber || this.service?.name || this.service?.address?.pickUpStationCode;
    },

    isClickAndCollectEligible() {
      const collectOptions = [SHIP_TO_STORE, PICK_FROM_STORE];
      const availabilityOptions = [PRODUCT_ALL_AVAILABLE, PRODUCT_NOT_ALL_AVAILABLE];

      if (this.isPDP) {
        return (
          this.product.availableForClickAndCollect &&
          collectOptions.includes(this.service?.clickAndCollectFeaturesInStore) &&
          inStock(this.product.stock.stockLevelStatus)
        );
      } else {
        return availabilityOptions.includes(
          this.service?.storeInfo?.productAvailability || this.service?.productsAvailability,
        );
      }
    },
  },

  methods: {
    onServiceClick() {
      if (this.isPDP) {
        this.$emit('plpServiceCardClick', this.service);
      }

      this.$emit('serviceCardClick', this.service);
    },
    onServiceKeydownEnter() {
      this.onServiceClick();
      this.$emit('focusConfirmButton');
    },
  },
});
</script>

<style lang="scss" scoped>
.service-location {
  cursor: pointer;
  position: relative;
  padding: $pad;
  border: 1px solid $color-action-border-neutral-default-base;
  border-radius: math.div($pad, 2);
  transition: border-color 0.2s linear;

  &:hover {
    border-color: $color-action-border-primary-default-base;
  }

  &.service-location--hide-border {
    border: none;
    padding: 0;
  }

  &.service-location--favorite,
  &.service-location--selected {
    padding: math.div($space, 2) 0.875 * $pad;
    border: solid 3px $color-action-border-primary-default-base;
  }

  &.service-location--checkout {
    cursor: pointer;

    .sc-availability {
      font-weight: map-get($font-weights, normal);
    }

    :deep(.store-opening-time-new) {
      flex-wrap: wrap;
    }
  }

  &.service-location--checkout-address-item {
    border: none;
    margin-top: $pad;
    padding: 0;
  }

  .sc-address-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: $pad;
  }

  :deep(.store-opening-time) {
    padding: 0;
  }

  .sc-availability {
    font-weight: map-get($font-weights, medium-bold);

    .sca-bubble {
      position: relative;
      display: inline-block;
      top: 1px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid transparent;
      margin-right: math.div($pad, 2);

      &.sca-bubble--result-no-info {
        border-color: $color-general-background-status-neutral-higher;
        &:before {
          content: '';
          position: absolute;
          top: 0px;
          left: 3px;
          rotate: -45deg;
          width: 2px;
          height: 100%;
          background-color: $color-general-background-status-neutral-higher;
        }
      }

      &.sca-bubble--result-zero {
        border-color: $color-action-border-error-default-base;
      }

      &.sca-bubble--result-available {
        border-color: $color-action-border-success-default-base;
        background: $color-general-background-status-positive-higher;
      }
    }
  }
}
</style>
